<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useNyalaCustomerVaultData } from '../../../../utilities/account/useNyalaCustomerVaultData'
import LoadingSpinner from '../../../base/LoadingSpinner.vue'
import { storeToRefs } from 'pinia'
import { RefreshIcon, ExternalLinkIcon } from '@heroicons/vue/outline'
import BaseInputText from '../../../base/BaseInputText.vue'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import BaseButtonPrimary from '../../../base/BaseButtonPrimary.vue'
import { resetNyalaPin } from '../../../../utilities/api/nyala-customer'
import { openAlertDialog, openConfirmDialog } from '../../../../utilities/useDialog'
import { i18n } from '../../../../localization'

const nyalaState = useNyalaCustomerVaultData()

const { id, is_created } = storeToRefs(nyalaState)

const loading = ref(false)

const pin = ref('')

onMounted(async () => {
    loading.value = true
    try {
        await nyalaState.fetchVaultData()
    } finally {
        loading.value = false
    }
})

async function resetPin() {
    if (useNyalaCustomerVaultData().isValidPin(pin.value) === false) {
        await openAlertDialog(
            i18n.global.t('nyala_vault.invalid_pin'),
            i18n.global.t('nyala_vault.enter_a_valid_pin'),
            {
                type: 'Error',
                hideCancel: true,
            }
        )
        pin.value = ''
        return
    }

    await resetNyalaPin(useNyalaCustomerVaultData().id, pin.value)
    await openConfirmDialog(i18n.global.t('nyala_vault.reset_initiated_info'), '', {
        type: 'Success',
        hideCancel: true,
    })
    pin.value = ''
}

async function generateRandomPin() {
    pin.value = await useNyalaCustomerVaultData().generateRandomPin()
}
</script>

<template>
    <div v-if="!loading">
        <div v-if="id === null" class="py-4 text-center text-gray-700">
            {{ $t('nyala_vault.nyala_vault_not_created') }}
        </div>
        <div v-else-if="is_created === false" class="py-4 text-center text-gray-700">
            {{ $t('nyala_vault.vault_not_created_yet_at_nyala') }}
        </div>
        <div v-else>
            <div class="px-6 py-4">
                <p class="text-md font-semibold text-gray-700">{{ $t('nyala_vault.reset_pin') }}</p>
                <div>
                    <div class="flex items-center space-x-4 pt-4">
                        <div class="w-1/2">
                            <BaseInputText v-model="pin" maxlength="6" name="pin" label="PIN-Code"></BaseInputText>
                        </div>
                        <p class="w-1/2 text-sm font-medium text-gray-500">
                            {{ $t('nyala_vault.pin_info') }}
                        </p>
                    </div>
                    <div class="flex justify-between pt-4">
                        <BaseButtonMuted @click="generateRandomPin">
                            <RefreshIcon class="mr-2 w-5"></RefreshIcon>
                            {{ $t('nyala_vault.generate_random_pin') }}
                        </BaseButtonMuted>
                        <BaseButtonPrimary @click="resetPin">{{ $t('nyala_vault.reset_pin') }}</BaseButtonPrimary>
                    </div>
                </div>
                <p class="text-md pb-4 pt-8 font-semibold text-gray-700">{{ $t('nyala_vault.explorer') }}</p>
                <a
                    v-if="nyalaState.explorer_url"
                    class="text-gray-500 hover:text-gray-600"
                    target="_blank"
                    :href="nyalaState.explorer_url"
                    >{{ $t('nyala_vault.explorer_link') }}
                    <ExternalLinkIcon class="inline-block w-5"></ExternalLinkIcon>
                </a>
                <p v-else>
                    {{ $t('nyala_vault.no_explorer_link') }}
                </p>
            </div>
        </div>
    </div>
    <div v-else>
        <LoadingSpinner class="h-10 w-10 fill-primary" />
    </div>
</template>
